import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Snapshot, ChecklistResponse, AgencyProfile, OnboardingAssessment } from '../types';

interface SnapshotContextValue {
  snapshot: Snapshot | null;
  isLoading: boolean;
  error: string | null;
  setSnapshot: (snapshot: Snapshot | null) => void;
  updateAgencyProfile: (profile: Partial<AgencyProfile>) => void;
  updateOnboarding: (onboarding: Partial<OnboardingAssessment>) => void;
  updateChecklistResponse: (itemId: string, response: Partial<ChecklistResponse>) => void;
  clearSnapshot: () => void;
}

const SnapshotContext = createContext<SnapshotContextValue | null>(null);

export function SnapshotProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshotState] = useState<Snapshot | null>(null);
  const [isLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setSnapshot = useCallback((newSnapshot: Snapshot | null) => {
    setSnapshotState(newSnapshot);
    setError(null);
  }, []);

  const updateAgencyProfile = useCallback((profile: Partial<AgencyProfile>) => {
    setSnapshotState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        agencyProfile: { ...prev.agencyProfile, ...profile },
        updatedAt: new Date(),
      };
    });
  }, []);

  const updateOnboarding = useCallback((onboarding: Partial<OnboardingAssessment>) => {
    setSnapshotState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        onboardingAssessment: { ...prev.onboardingAssessment, ...onboarding },
        updatedAt: new Date(),
      };
    });
  }, []);

  const updateChecklistResponse = useCallback(
    (itemId: string, response: Partial<ChecklistResponse>) => {
      setSnapshotState(prev => {
        if (!prev) return prev;
        const existingResponse = prev.checklistResponses[itemId] || {
          itemId,
          status: 'UNKNOWN' as const,
          updatedAt: new Date(),
        };
        return {
          ...prev,
          checklistResponses: {
            ...prev.checklistResponses,
            [itemId]: {
              ...existingResponse,
              ...response,
              updatedAt: new Date(),
            },
          },
          updatedAt: new Date(),
        };
      });
    },
    []
  );

  const clearSnapshot = useCallback(() => {
    setSnapshotState(null);
    setError(null);
  }, []);

  return (
    <SnapshotContext.Provider
      value={{
        snapshot,
        isLoading,
        error,
        setSnapshot,
        updateAgencyProfile,
        updateOnboarding,
        updateChecklistResponse,
        clearSnapshot,
      }}
    >
      {children}
    </SnapshotContext.Provider>
  );
}

export function useSnapshotContext() {
  const context = useContext(SnapshotContext);
  if (!context) {
    throw new Error('useSnapshotContext must be used within a SnapshotProvider');
  }
  return context;
}
